import { useState } from "react";
import { cn } from "../../lib/utils";
import { OwnerTag } from "./OwnerTag";
import { Badge } from "./Badge";
import {
  IconColorPalette,
  IconDocFolder,
  IconFiles,
  IconPen,
  IconImage,
  IconFolder,
} from "nucleo-glass";
import { NucleoIcon } from "./NucleoIcon";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  Download01Icon,
  Edit01Icon,
  Delete01Icon,
  FolderTransferIcon,
} from "@hugeicons/core-free-icons";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import {
  isPreviewableImage,
  isPreviewablePdf,
  formatFileSize,
  formatRelativeTime,
  getCategoryLabel,
} from "../../lib/assetUtils";
import type { AssetCategory } from "../../lib/assetUtils";
import { PdfThumbnail } from "./PdfThumbnail";
import type { Owner } from "../../data/types";
import type { ReactNode } from "react";

// ── Category icon map (nucleo-glass) ────────────────────────────

const categoryIcons: Record<AssetCategory, () => ReactNode> = {
  branding: () => <NucleoIcon icon={<IconColorPalette size={28} />} />,
  documents: () => <NucleoIcon icon={<IconDocFolder size={28} />} />,
  decks: () => <NucleoIcon icon={<IconFiles size={28} />} />,
  notes: () => <NucleoIcon icon={<IconPen size={28} />} />,
  images: () => <NucleoIcon icon={<IconImage size={28} />} />,
  other: () => <NucleoIcon icon={<IconFolder size={28} />} />,
};

// ── Dropdown menu item styling ──────────────────────────────────

const menuItemClass = cn(
  "flex items-center gap-[var(--spacing-2)] px-[var(--spacing-2_5)] py-[var(--spacing-1_5)]",
  "text-[13px] text-[var(--color-text-secondary)] rounded-[var(--radius-sm)]",
  "cursor-pointer select-none outline-none",
  "data-[highlighted]:bg-[var(--color-bg-hover)] data-[highlighted]:text-[var(--color-text-primary)]",
  "transition-colors duration-[var(--duration-fast)]",
);

const menuItemDangerClass = cn(
  "flex items-center gap-[var(--spacing-2)] px-[var(--spacing-2_5)] py-[var(--spacing-1_5)]",
  "text-[13px] text-red-400 rounded-[var(--radius-sm)]",
  "cursor-pointer select-none outline-none",
  "data-[highlighted]:bg-red-500/10 data-[highlighted]:text-red-300",
  "transition-colors duration-[var(--duration-fast)]",
);

// ── Animated dots → chevron icon ────────────────────────────────

function AnimatedMenuIcon({ open }: { open: boolean }) {
  return (
    <div className="relative w-[14px] h-[14px] flex items-center justify-center">
      {/* Three dots (vertical) */}
      <div
        className={cn(
          "absolute inset-0 flex flex-col items-center justify-center gap-[2px]",
          "transition-all duration-[var(--duration-medium)] ease-[cubic-bezier(0.2,0,0,1)]",
          open ? "opacity-0 scale-75 rotate-90" : "opacity-100 scale-100 rotate-0",
        )}
      >
        <div className="w-[3px] h-[3px] rounded-full bg-current" />
        <div className="w-[3px] h-[3px] rounded-full bg-current" />
        <div className="w-[3px] h-[3px] rounded-full bg-current" />
      </div>

      {/* Chevron down (>) rotated */}
      <svg
        width="14"
        height="14"
        viewBox="0 0 14 14"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={cn(
          "absolute transition-all duration-[var(--duration-medium)] ease-[cubic-bezier(0.2,0,0,1)]",
          open ? "opacity-100 scale-100 rotate-0" : "opacity-0 scale-75 -rotate-90",
        )}
      >
        <path d="M3.5 5.25L7 8.75L10.5 5.25" />
      </svg>
    </div>
  );
}

// ── Props ───────────────────────────────────────────────────────

interface AssetCardProps {
  id: string;
  title: string;
  description: string;
  category: AssetCategory;
  owner: Owner;
  fileName?: string;
  fileType?: string;
  fileSize?: number;
  fileUrl?: string | null;
  createdAt: number;
  viewMode: "grid" | "list";
  onClick: (id: string) => void;
  onDownload?: (id: string) => void;
  onEdit?: (id: string) => void;
  onMove?: (id: string) => void;
  onDelete?: (id: string) => void;
}

// ── Three-Dots Menu ─────────────────────────────────────────────

function AssetActionsMenu({
  id,
  title,
  hasFile,
  onDownload,
  onEdit,
  onMove,
  onDelete,
  variant = "overlay",
}: {
  id: string;
  title: string;
  hasFile: boolean;
  onDownload?: (id: string) => void;
  onEdit?: (id: string) => void;
  onMove?: (id: string) => void;
  onDelete?: (id: string) => void;
  variant?: "overlay" | "inline";
}) {
  const [open, setOpen] = useState(false);

  return (
    <DropdownMenu.Root open={open} onOpenChange={setOpen}>
      <DropdownMenu.Trigger asChild>
        <button
          onClick={(e) => e.stopPropagation()}
          className={cn(
            "p-[var(--spacing-1_5)] rounded-[var(--radius-sm)]",
            "text-[var(--color-icon)] hover:text-[var(--color-text-primary)]",
            "transition-all duration-[var(--duration-fast)]",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent-focus)]",
            variant === "overlay" && [
              "bg-[var(--color-bg-canvas)]/80 backdrop-blur-sm",
              "border border-[var(--color-border-stroke)]",
            ],
            variant === "inline" && "hover:bg-[var(--color-bg-hover)]",
            // Keep visible when open
            open && "opacity-100 !text-[var(--color-text-primary)]",
            open && variant === "overlay" && "bg-[var(--color-bg-canvas)] border-[var(--color-accent-400)]",
            open && variant === "inline" && "bg-[var(--color-bg-hover)]",
          )}
          aria-label={`Actions for ${title}`}
        >
          <AnimatedMenuIcon open={open} />
        </button>
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          sideOffset={4}
          align="end"
          onClick={(e) => e.stopPropagation()}
          className={cn(
            "z-50 min-w-[160px] p-[var(--spacing-1)]",
            "rounded-[var(--radius-md)] border border-[var(--color-border-stroke)]",
            "bg-[var(--color-bg-surface)] shadow-lg",
            "animate-dropdown-in origin-top-right",
          )}
        >
          {hasFile && onDownload && (
            <DropdownMenu.Item
              className={menuItemClass}
              onSelect={() => onDownload(id)}
            >
              <HugeiconsIcon icon={Download01Icon} size={14} />
              Download
            </DropdownMenu.Item>
          )}

          {onEdit && (
            <DropdownMenu.Item
              className={menuItemClass}
              onSelect={() => onEdit(id)}
            >
              <HugeiconsIcon icon={Edit01Icon} size={14} />
              Edit
            </DropdownMenu.Item>
          )}

          {onMove && (
            <DropdownMenu.Item
              className={menuItemClass}
              onSelect={() => onMove(id)}
            >
              <HugeiconsIcon icon={FolderTransferIcon} size={14} />
              Move
            </DropdownMenu.Item>
          )}

          {onDelete && (
            <>
              <DropdownMenu.Separator className="h-px my-[var(--spacing-1)] bg-[var(--color-border-stroke)]" />
              <DropdownMenu.Item
                className={menuItemDangerClass}
                onSelect={() => onDelete(id)}
              >
                <HugeiconsIcon icon={Delete01Icon} size={14} />
                Delete
              </DropdownMenu.Item>
            </>
          )}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

// ── Component ───────────────────────────────────────────────────

export function AssetCard({
  id,
  title,
  description,
  category,
  owner,
  fileName,
  fileType,
  fileSize,
  fileUrl,
  createdAt,
  viewMode,
  onClick,
  onDownload,
  onEdit,
  onMove,
  onDelete,
}: AssetCardProps) {
  const hasPreviewableImage = isPreviewableImage(fileType) && fileUrl;
  const hasPreviewablePdf = isPreviewablePdf(fileType) && fileUrl;
  const CategoryIcon = categoryIcons[category];
  const hasFile = Boolean(fileUrl);

  function handleDragStart(e: React.DragEvent) {
    e.dataTransfer.setData("application/x-asset-id", id);
    e.dataTransfer.effectAllowed = "move";
    // Add drag styling after a tick so it doesn't affect the drag image
    requestAnimationFrame(() => {
      (e.target as HTMLElement).classList.add("dragging-asset");
    });
  }

  function handleDragEnd(e: React.DragEvent) {
    (e.target as HTMLElement).classList.remove("dragging-asset");
  }

  if (viewMode === "list") {
    return (
      <div
        role="button"
        tabIndex={0}
        draggable
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onClick={() => onClick(id)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onClick(id);
          }
        }}
        className={cn(
          "w-full flex items-center gap-[var(--spacing-3)] px-[var(--spacing-3)] py-[var(--spacing-2)]",
          "rounded-[var(--radius-md)] border border-[var(--color-border-stroke)]",
          "bg-[var(--color-bg-surface)] hover:bg-[var(--color-bg-hover)]",
          "transition-colors duration-[var(--duration-fast)] cursor-pointer text-left group",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent-focus)] focus-visible:ring-offset-2",
        )}
      >
        {/* Thumbnail / icon */}
        <div className="w-[40px] h-[40px] rounded-[var(--radius-sm)] overflow-hidden shrink-0 flex items-center justify-center bg-[var(--color-bg-hover)]">
          {hasPreviewableImage ? (
            <img src={fileUrl} alt={title} draggable={false} className="w-full h-full object-cover" />
          ) : hasPreviewablePdf ? (
            <PdfThumbnail url={fileUrl!} width={40} className="w-full h-full" />
          ) : (
            <span className="text-[var(--color-accent-300)]">
              <CategoryIcon />
            </span>
          )}
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <p className="text-[14px] font-medium text-[var(--color-text-primary)] truncate">{title}</p>
          <p className="text-[13px] text-[var(--color-text-muted)] truncate">
            {fileName ?? getCategoryLabel(category)}
            {fileSize ? ` · ${formatFileSize(fileSize)}` : ""}
          </p>
        </div>

        {/* Meta */}
        <div className="flex items-center gap-[var(--spacing-2)] shrink-0">
          <Badge variant="default">{getCategoryLabel(category)}</Badge>
          <OwnerTag owner={owner} />
          <span className="text-[12px] text-[var(--color-text-muted)] tabular-nums whitespace-nowrap">
            {formatRelativeTime(createdAt)}
          </span>
          <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-[var(--duration-fast)]">
            <AssetActionsMenu
              id={id}
              title={title}
              hasFile={hasFile}
              onDownload={onDownload}
              onEdit={onEdit}
              onMove={onMove}
              onDelete={onDelete}
              variant="inline"
            />
          </div>
        </div>
      </div>
    );
  }

  // ── Grid view ──
  return (
    <div
      role="button"
      tabIndex={0}
      draggable
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onClick={() => onClick(id)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick(id);
        }
      }}
      className={cn(
        "flex flex-col rounded-[var(--radius-lg)] border border-[var(--color-border-stroke)]",
        "bg-[var(--color-bg-surface)] hover:bg-[var(--color-bg-hover)]",
        "transition-all duration-[var(--duration-fast)] cursor-pointer text-left overflow-hidden group",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent-focus)] focus-visible:ring-offset-2",
      )}
    >
      {/* Preview area */}
      <div className="relative w-full aspect-[4/3] flex items-center justify-center bg-[var(--color-bg-hover)] overflow-hidden">
        {hasPreviewableImage ? (
          <img
            src={fileUrl}
            alt={title}
            draggable={false}
            className="w-full h-full object-cover transition-transform duration-[var(--duration-slow)] group-hover:scale-[1.02]"
          />
        ) : hasPreviewablePdf ? (
          <PdfThumbnail url={fileUrl!} width={220} className="w-full h-full" />
        ) : (
          <span className="text-[var(--color-accent-300)] opacity-60">
            <CategoryIcon />
          </span>
        )}

        {/* Three-dots menu overlay */}
        <div
          className={cn(
            "absolute top-[var(--spacing-2)] right-[var(--spacing-2)]",
            "opacity-0 group-hover:opacity-100 transition-opacity duration-[var(--duration-fast)]",
            "[&:has([data-state=open])]:opacity-100",
          )}
        >
          <AssetActionsMenu
            id={id}
            title={title}
            hasFile={hasFile}
            onDownload={onDownload}
            onEdit={onEdit}
            onMove={onMove}
            onDelete={onDelete}
            variant="overlay"
          />
        </div>
      </div>

      {/* Card body */}
      <div className="p-[var(--spacing-3)] flex flex-col gap-[var(--spacing-1_5)]">
        <p className="text-[14px] font-medium text-[var(--color-text-primary)] truncate">{title}</p>
        {description && (
          <p className="text-[13px] text-[var(--color-text-muted)] line-clamp-2 leading-[18px]">{description}</p>
        )}
        <div className="flex items-center gap-[var(--spacing-2)] mt-[var(--spacing-0_5)]">
          <Badge variant="default">{getCategoryLabel(category)}</Badge>
          <OwnerTag owner={owner} />
          {fileSize ? (
            <span className="text-[12px] text-[var(--color-text-muted)] tabular-nums ml-auto">
              {formatFileSize(fileSize)}
            </span>
          ) : null}
        </div>
      </div>
    </div>
  );
}
